import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi, accountApi, subscriptionsApi, setAccessToken, getAccessToken } from '../services/api';
import { useTheme, Theme } from './ThemeContext';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

interface SubscriptionInfo {
  plan_type: string;
  plan_label: string;
  status: string;
  is_beta?: boolean;
  beta_ends_at: string | null;
  trial_ends_at: string | null;
  trial_days_remaining: number | null;
  features: string[];
  max_users?: number;
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  onboarding_completed?: boolean;
  demo_data_seeded?: boolean;
  mfa_enabled?: boolean;
  business_name?: string;
  practice_id?: string;
  practice_role?: string; // owner, admin, coach, assistenz
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  subscription: SubscriptionInfo | null;
  login: (email: string, password: string) => Promise<{ mfa_required?: boolean; mfa_method?: string }>;
  loginWithCode: (email: string, code: string) => Promise<void>;
  loginWithTotp: (email: string, password: string, totpCode: string) => Promise<void>;
  register: (data: { email: string; password: string; first_name: string; last_name: string; consent_terms?: boolean; consent_privacy?: boolean; consent_newsletter?: boolean }) => Promise<void>;
  logout: () => void;
  refreshSubscription: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(getAccessToken());
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const { setTheme } = useTheme();

  const loadTheme = useCallback(async () => {
    try {
      const res = await accountApi.getTheme();
      if (res.data?.theme) {
        setTheme(res.data.theme as Theme);
      }
    } catch { /* ignore — use localStorage fallback */ }
  }, [setTheme]);

  const loadSubscription = useCallback(async () => {
    try {
      const res = await subscriptionsApi.me();
      setSubscription(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchUser = useCallback(async () => {
    // If no in-memory token, try refresh from cookie
    if (!getAccessToken()) {
      try {
        const refreshRes = await authApi.refresh();
        const refreshedToken = refreshRes.data?.access_token;
        if (refreshedToken) {
          setAccessToken(refreshedToken);
          setToken(refreshedToken);
        }
      } catch {
        // No valid refresh cookie — user needs to log in
        setLoading(false);
        return;
      }
    }

    try {
      const res = await authApi.me();
      setUser(res.data);
      loadTheme();
      loadSubscription();
    } catch {
      setAccessToken(null);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [loadTheme, loadSubscription]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const _applyToken = (newToken: string) => {
    setAccessToken(newToken);
    setToken(newToken);
  };

  const login = async (email: string, password: string): Promise<{ mfa_required?: boolean; mfa_method?: string }> => {
    const res = await authApi.login({ email, password });
    if (res.data.mfa_required) {
      return { mfa_required: true, mfa_method: res.data.mfa_method || 'totp' };
    }
    _applyToken(res.data.access_token);
    const meRes = await authApi.me();
    setUser(meRes.data);
    loadTheme();
    loadSubscription();
    return {};
  };

  const loginWithTotp = async (email: string, password: string, totpCode: string) => {
    const res = await authApi.loginTotp(email, password, totpCode);
    _applyToken(res.data.access_token);
    const meRes = await authApi.me();
    setUser(meRes.data);
    loadTheme();
    loadSubscription();
  };

  const loginWithCode = async (email: string, code: string) => {
    const res = await authApi.verifyCode(email, code);
    _applyToken(res.data.access_token);
    const meRes = await authApi.me();
    setUser(meRes.data);
    loadTheme();
    loadSubscription();
  };

  const register = async (data: { email: string; password: string; first_name: string; last_name: string; consent_terms?: boolean; consent_privacy?: boolean; consent_newsletter?: boolean }) => {
    const res = await authApi.register(data);
    _applyToken(res.data.access_token);
    const meRes = await authApi.me();
    setUser(meRes.data);
    loadTheme();
    loadSubscription();
  };

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    setAccessToken(null);
    setToken(null);
    setUser(null);
    setSubscription(null);
  }, []);

  // Idle timeout — auto-logout after 30 min inactivity
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        logout();
      }, IDLE_TIMEOUT_MS);
    };

    const events: (keyof WindowEventMap)[] = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user, logout]);

  const refreshSubscription = async () => {
    await loadSubscription();
  };

  const refreshUser = async () => {
    try {
      const res = await authApi.me();
      setUser(res.data);
    } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, subscription,
      login, loginWithCode, loginWithTotp, register, logout,
      refreshSubscription, refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
